/**
 * <cart-upsell> — the cart drawer's "you may also like" recommendation carousel.
 *
 * Lives inside the cart drawer (sections/cart-drawer.liquid). On demand — when the drawer
 * opens or the cart changes — it seeds Shopify's Product Recommendations API with the first
 * cart line item's product, fetches the `cart-upsell` section rendered with those
 * recommendations, injects the carousel, and inits Swiper.
 *
 * Nothing loads until the drawer is actually opened (the element is global via the cart
 * drawer, so eagerly fetching/loading Swiper on every page view would be wasted work).
 */
import { ThemeEvents } from '@theme/events';

/** @type {Promise<any> | null} */
let swiperLoading = null;

/**
 * Lazily loads the self-hosted Swiper bundle (JS + CSS) once, the first time a carousel
 * actually needs it — so ~150KB isn't shipped on every page just because the cart drawer
 * (which hosts this element) is global.
 *
 * @param {string} jsUrl
 * @param {string} cssUrl
 * @returns {Promise<any>} Resolves with the Swiper constructor.
 */
function ensureSwiper(jsUrl, cssUrl) {
  if (window.Swiper) return Promise.resolve(window.Swiper);
  if (swiperLoading) return swiperLoading;

  swiperLoading = new Promise((resolve, reject) => {
    if (cssUrl && !document.querySelector(`link[href="${cssUrl}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${jsUrl}"]`);
    if (!existing) {
      const script = document.createElement('script');
      script.src = jsUrl;
      script.addEventListener('error', () => reject(new Error('Swiper failed to load')));
      document.head.appendChild(script);
    }

    // Poll with setTimeout (NOT requestAnimationFrame — rAF is throttled/paused in
    // background or headless tabs, so it can miss window.Swiper appearing). This also
    // covers Swiper being provided by a different script already on the page (e.g. the
    // CDN copy loaded by editorial-feature).
    let waited = 0;
    const check = () => {
      if (window.Swiper) {
        resolve(window.Swiper);
      } else if (waited > 8000) {
        reject(new Error('Swiper failed to load'));
      } else {
        waited += 100;
        setTimeout(check, 100);
      }
    };
    check();
  });

  return swiperLoading;
}

class CartUpsell extends HTMLElement {
  /** @type {string | null} The product id the currently-shown recommendations were built for. */
  #loadedSeed = null;
  /** @type {any} */
  #swiper = null;
  #loading = false;
  /** A cart update that arrived mid-load — re-run once the current load finishes. */
  #pending = false;
  #cart = null;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#load);
    document.addEventListener('cart-drawer:open', this.#load);

    // If the drawer is already open when this (re)connects — e.g. after an add-to-cart
    // re-render — load straight away.
    if (document.body.classList.contains('cart-drawer-open')) this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#load);
    document.removeEventListener('cart-drawer:open', this.#load);
    this.#swiper?.destroy?.(true, true);
  }

  #load = async (event) => {
    if (
      event?.type === 'cart-drawer:open' &&
      event.detail?.cartUpdate &&
      (this.#loading || this.querySelector('[data-cart-upsell-carousel]'))
    ) {
      return;
    }

    const cart = event?.detail?.resource;
    this.#cart = Array.isArray(cart?.items) ? cart : null;

    // Don't drop overlapping updates (e.g. add-from-PDP + an immediate remove): mark a
    // re-run so the carousel always ends on the LATEST cart state instead of a stale seed.
    if (this.#loading) {
      this.#pending = true;
      return;
    }
    this.#loading = true;
    try {
      do {
        this.#pending = false;
        await this.#loadOnce();
      } while (this.#pending);
    } catch (error) {
      console.error('Cart upsell error:', error);
    } finally {
      this.#loading = false;
    }
  };

  async #loadOnce() {
    const seed = await this.#firstCartProductId();

    // Empty cart — nothing to recommend against (the empty state owns its own carousel).
    if (!seed) {
      this.#swiper?.destroy?.(true, true);
      this.#swiper = null;
      this.replaceChildren();
      this.#loadedSeed = null;
      return;
    }

    // Already showing recommendations for this product — don't refetch.
    if (seed === this.#loadedSeed && this.querySelector('[data-cart-upsell-carousel]')) return;

    const response = await fetch(`${this.dataset.url}&product_id=${seed}`);
    if (!response.ok) return;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const content = doc.querySelector('.cart-upsell__content');

    // Out-of-stock recommendations render dimmed by default; when the drawer's
    // "Show out-of-stock products" toggle is off (data-show-unavailable !== 'true'),
    // drop those slides entirely so Swiper never lays them out.
    if (content && this.dataset.showUnavailable !== 'true') {
      content
        .querySelectorAll('.cart-drawer-upsell--unavailable')
        .forEach((card) => card.closest('.cart-upsell__slide')?.remove());
    }

    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;

    // No content, or every recommendation was out of stock and hidden.
    if (!content || !content.querySelector('.cart-upsell__slide')) {
      this.replaceChildren();
      this.#loadedSeed = seed;
      return;
    }

    // Inject the .cart-upsell__content wrapper itself (not just its children) so its
    // divider/spacing styling applies. replaceChildren adopts it from the parsed doc.
    this.replaceChildren(content);
    this.#loadedSeed = seed;
    await this.#initCarousel();

    // Swym's script only scans the DOM once on load, so the wishlist buttons on these
    // freshly-injected cards stay unclickable until reinitialised — see swym-custom.liquid.
    document.dispatchEvent(new CustomEvent('cart-upsell:loaded'));
  }

  /**
   * The authoritative first cart line item's product id (via /cart.js), so the seed stays
   * correct after AJAX add/remove regardless of DOM timing.
   * @returns {Promise<string | null>}
   */
  async #firstCartProductId() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const knownCart = this.#cart;
      this.#cart = null;
      const cart = knownCart ?? (await fetch(`${root}cart.js`).then((response) => response.json()));
      const productId = cart.items?.[0]?.product_id;
      return productId ? String(productId) : null;
    } catch {
      return null;
    }
  }

  async #initCarousel() {
    const carousel = this.querySelector('[data-cart-upsell-carousel]');
    if (!(carousel instanceof HTMLElement)) return;

    const Swiper = await ensureSwiper(this.dataset.swiperJs, this.dataset.swiperCss);

    this.#swiper = new Swiper(carousel, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      grabCursor: true,
    });
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}

/**
 * <cart-empty-carousel> — the empty-cart state's "you may like these" carousel.
 *
 * Simpler than <cart-upsell>: its slides are server-rendered straight from the drawer's
 * collection setting (no recommendations fetch), so it just needs to init Swiper whenever
 * it's inserted — both on first paint AND when component-cart-items.js clones the empty
 * state from #empty-cart-template after the last item is removed (a fresh clone re-upgrades
 * and re-runs connectedCallback, so Swiper re-inits cleanly).
 */
class CartEmptyCarousel extends HTMLElement {
  /** @type {any} */
  #swiper = null;

  async connectedCallback() {
    const carousel = this.querySelector('[data-cart-empty-carousel]');
    if (!(carousel instanceof HTMLElement)) return;

    try {
      const Swiper = await ensureSwiper(this.dataset.swiperJs, this.dataset.swiperCss);
      if (!this.isConnected) return;
      this.#swiper = new Swiper(carousel, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        grabCursor: true,
      });
    } catch (error) {
      console.error('Cart empty carousel error:', error);
    }
  }

  disconnectedCallback() {
    this.#swiper?.destroy?.(true, true);
  }
}

if (!customElements.get('cart-empty-carousel')) {
  customElements.define('cart-empty-carousel', CartEmptyCarousel);
}
